import React, { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import spinnerSvgPath from '../../assets/images/spinner.svg';
import noImageSvgPath from '../../assets/images/no_image.svg';
import './ThumbnailCard.css';

export type ThumbnailCardProps = {
  thumbnailUrl: string;
  text: string;
  thumbnailSize?: { width: number; height: number };
  onClick: () => void;
  showLoader?: boolean;
};

const ThumbnailCard: React.FC<ThumbnailCardProps> = ({
  thumbnailUrl,
  text,
  thumbnailSize = { width: 150, height: 178 },
  onClick,
  showLoader = true,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleError = () => {
    setError('error');
    setIsLoading(false);
  };

  const handleClick = (event: { preventDefault: () => void }) => {
    // Block interaction while still loading or on error
    if (isLoading || error) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    onClick();
  };

  const imgRef = useRef<HTMLImageElement | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const loadingRef = useRef<boolean>(isLoading);

  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    setError(null);
    setIsLoading(true);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      if (loadingRef.current) {
        handleError();
      }
    }, 60000);
  }, [thumbnailUrl]);

  useEffect(() => {
    if (imgRef.current && imgRef.current.complete) {
      setIsLoading(false);
    }
  }, [thumbnailUrl]);

  useEffect(() => {
    if (!isLoading || error) {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isLoading, error]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div
        data-tooltip-id={text}
        data-tooltip-content={text}
        className={`group overflow-hidden rounded-cds-4 inline-block align-top thumbnail-card-root thumb-w-${thumbnailSize.width}`}
        data-testid="thumbnail-card"
        onClick={handleClick}
        tabIndex={0}
      >
        <a
          href="#"
          tabIndex={-1}
          className="block w-full leading-none"
        >
          <div
            className={`relative w-full thumbnail-wrapper-dynamic thumb-h-${thumbnailSize.height}`}
            data-testid="thumbnail-wrapper"
          >
            <div
              className={`absolute inset-0 flex items-center justify-center thumbnail-placeholder-base ${error || isLoading ? 'thumb-state-loading-error' : 'thumb-state-loaded'} ${error || isLoading ? 'thumb-radius-top' : 'thumb-radius-all'}`}
              data-testid="thumbnail-placeholder"
            >
              {error ? (
                <div
                  className="flex items-center justify-center thumbnail-fallback"
                  data-testid="thumbnail-fallback"
                >
                  <img
                    src={noImageSvgPath}
                    alt="No image"
                    width={56}
                    height={56}
                    className="flex-shrink-0"
                    data-testid="thumbnail-fallback-image"
                  />
                </div>
              ) : (
                showLoader && (
                  <div className="flex justify-center items-center flex-shrink-0 thumbnail-spinner-wrapper">
                    <img
                      src={spinnerSvgPath}
                      alt="Loading..."
                      className="w-[50px] h-[50px] animate-spin select-none"
                      draggable={false}
                      tabIndex={-1}
                      data-testid="thumbnail-spinner"
                    />
                  </div>
                )
              )}
            </div>
            <img
              ref={imgRef}
              src={thumbnailUrl}
              alt={text || 'Thumbnail'}
              width={thumbnailSize.width}
              height={thumbnailSize.height}
              onLoad={() => setIsLoading(false)}
              onError={handleError}
              className={`block w-full h-full object-cover thumbnail-image-base ${error ? 'opacity-0' : ''}`}
              data-testid="thumbnail-image"
              decoding="async"
            />
          </div>
        </a>
        <div
          className="px-2 py-1 flex items-center rounded-b-cds-4 bg-white group-hover:bg-[var(--cds-surface-container-3)] h-[60px]"
          data-testid="thumbnail-footer"
        >
          <div
            className="font-sans font-normal text-cds-on-surface-2 text-[var(--cds-font-scale-neg-1)] leading-[1.15] line-clamp-2 overflow-hidden text-center w-full thumbnail-footer-text"
            data-testid="thumbnail-footer-text"
          >
            {text}
          </div>
        </div>
      </div>
      <Tooltip
        id={text}
        place="top"
        className="tooltip-high-z"
        positionStrategy="fixed"
      />
    </>
  );
};

export default ThumbnailCard;
